import { FilesystemSection } from "@/components/CodeGen/Chat/components/FilesystemSection";
import { STAGED_FILES_SNAPSHOT_ID } from "@/constants";
import {
  getProjectSnapshotQueryOptions,
  getProjectSnapshotsQueryOptions,
} from "@/lib/hooks/useFilesystem";
import { useFilesystemStore } from "@/state/stores";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

export const searchSchema = z.object({
  tab: z.enum(["spec", "code", "preview", "trace"]).default("spec"),
  file: z.string().optional(),
  traceId: z.string().optional(),
});

export type FilesystemSearchParams = z.infer<typeof searchSchema>;

export const Route = createFileRoute(
  "/(codegen)/codegen/$projectId/filesystem/$snapshotId",
)({
  component: FilesystemView,
  validateSearch: searchSchema,
  loader: async ({
    context: { queryClient },
    params: { projectId, snapshotId },
  }) => {
    // Always ensure snapshots list is loaded first
    const snapshotsResponse = await queryClient.ensureQueryData(
      getProjectSnapshotsQueryOptions(projectId),
    );

    if (snapshotsResponse?.data?.snapshots) {
      // Populate snapshots in store
      const { setSnapshots } = useFilesystemStore.getState();
      setSnapshots(snapshotsResponse.data.snapshots);
    }

    // HACK - If we fetch the staged files here, it hits a query cache, which is out of sync with the store
    //        Let's just assume we have the correct staged files locally already, since they are fetched in the message cache sync
    if (snapshotId === STAGED_FILES_SNAPSHOT_ID) {
      return;
    }

    // For specific snapshots, load the snapshot data
    const snapshotResponse = await queryClient.ensureQueryData(
      getProjectSnapshotQueryOptions(projectId, snapshotId),
    );

    if (snapshotResponse?.data?.snapshot?.files) {
      // Populate the store with the snapshot files
      const { upsertSnapshotFiles } = useFilesystemStore.getState();
      upsertSnapshotFiles(snapshotId, snapshotResponse.data.snapshot.files);
    }
  },
});

export function FilesystemView() {
  return <FilesystemSection />;
}
